import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { useState, useEffect } from "react";
import { AuthProvider } from "@/contexts/AuthContext";
import { VendorFilterProvider } from "@/contexts/VendorFilterContext";
import { TenantProvider } from "@/lib/pro";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Landing from "@/pages/Landing";
import OptInPolicy from "@/pages/OptInPolicy";
import AdminCsvSyncHistory from "@/pages/AdminCsvSyncHistory";
import CommandPalette from "@/components/CommandPalette";
import WorkerDashboard from "@/pages/WorkerDashboard";
import WorkerDashboardV2 from "@/pages/WorkerDashboardV2";
import JobDetail from "@/pages/JobDetail";
import WorkerJobViewV4 from "@/pages/WorkerJobViewV4";
import MapView from "@/pages/MapView";
import LiveField from "@/pages/LiveField";
import TenantLogin from "@/pages/TenantLogin";
import LandlordLogin from "@/pages/LandlordLogin";
import Settings from "@/pages/Settings";
import BillingSettings from "@/pages/BillingSettings";
import Trust from "@/pages/Trust";
import PublicPhotos from "@/pages/PublicPhotos";
import PublicBid from "@/pages/PublicBid";
import PublicInspectionReport from "@/pages/PublicInspectionReport";
import PropertyInspection from "@/pages/PropertyInspection";
import AdminPhotoSearch from "@/pages/AdminPhotoSearch";
import ProspectRadar from "@/pages/ProspectRadar";
import ApplicationPublic from "@/pages/ApplicationPublic";
import VacancyRadar from "@/pages/VacancyRadar";
import VacantSecurity from "@/pages/VacantSecurity";
import AdminCameras from "@/pages/AdminCameras";
import PropertyWalk from "@/pages/PropertyWalk";
import RentcastPreview from "@/pages/RentcastPreview";
import TurnKits from "@/pages/TurnKits";
import ProjectRadar from "@/pages/ProjectRadar";
import ProjectRadarDetail from "@/pages/ProjectRadarDetail";
import PublicProjectView from "@/pages/PublicProjectView";
import CleanerPortal from "@/pages/CleanerPortal";  // legacy — kept for import stability, superseded by TradesmanPortal
import TradesmanPortal from "@/pages/TradesmanPortal";
import TradesmanSignedOut from "@/pages/TradesmanSignedOut";
import ChewStudentPortal from "@/pages/ChewStudentPortal";
import LandlordPortalView from "@/pages/LandlordPortalView";
import LandlordPropertyDetail from "@/pages/LandlordPropertyDetail";
import LandlordInbox from "@/pages/LandlordInbox";
import TenantBranding from "@/pages/TenantBranding";
import SuperAdmin from "@/pages/SuperAdmin";
import DemosHub from "@/pages/DemosHub";
import BrandMotionMock from "@/pages/BrandMotionMock";
import LandlordQuoteApproval from "@/pages/LandlordQuoteApproval";
import PMRadar from "@/pages/PMRadar";
import OwnerStatements from "@/pages/OwnerStatements";
import AdminActivity from "@/pages/AdminActivity";
import TurnDashboard from "@/pages/TurnDashboard";
import TechTurns from "@/pages/TechTurns";
import AdminTurnsBoard from "@/pages/AdminTurnsBoard";
import AdminTurnsBoardV2 from "@/pages/AdminTurnsBoardV2";
import AdminPurgeTruckSheet from "@/pages/AdminPurgeTruckSheet";
import AdminLiveFeed from "@/pages/AdminLiveFeed";
import AdminApplianceIQ from "@/pages/AdminApplianceIQ";
import InspectionsBoard from "@/pages/Inspections";
import { PropertiesList, PropertyDetail } from "@/pages/Properties";
import PropertyDetailV2 from "@/pages/PropertyDetailV2";
import { AdminDashboard, AdminJobs, AdminJobDetail, AdminWorkers, AdminReports } from "@/pages/Admin";
import AdminDashboardV2 from "@/pages/AdminDashboardV2";
import AdminJobDetailV2 from "@/pages/AdminJobDetailV2";
import AdminJobLegalProtocol from "@/pages/AdminJobLegalProtocol";
import AdminJobsV2 from "@/pages/AdminJobsV2";
import AdminDuplicates from "@/pages/AdminDuplicates";
import AdminPortfolioMergePreview from "@/pages/AdminPortfolioMergePreview";
import AdminJobsDedupe from "@/pages/AdminJobsDedupe";
import SafetyBoard from "@/pages/SafetyBoard";
import AdminSubContractors from "@/pages/AdminSubContractors";
import AdminInbox from "@/pages/AdminInbox";
import TeamChat from "@/pages/TeamChat";
import SubPortal from "@/pages/SubPortal";
import AdminLineup from "@/pages/Lineup";
import LineupV2 from "@/pages/LineupV2";
import DesignPreview from "@/pages/DesignPreview";
import DesignPreviewStub from "@/pages/DesignPreviewStub";
import WorkOrdersPreview from "@/pages/WorkOrdersPreview";
import WorkOrderDetailPreview from "@/pages/WorkOrderDetailPreview";
import PropertyDetailPreview from "@/pages/PropertyDetailPreview";
import AdminScheduleWeek from "@/pages/AdminScheduleWeek";
import AdminScheduleMonth from "@/pages/AdminScheduleMonth";
import AdminJobDesktop from "@/pages/AdminJobDesktop";
import AdminLandlords from "@/pages/AdminLandlords";
import AdminLandlordDetail from "@/pages/AdminLandlordDetail";
import DraftsQueue from "@/pages/DraftsQueue";
import AdminInsurance from "@/pages/AdminInsurance";
import PhillyComplianceRadar from "@/pages/PhillyComplianceRadar";
import BusinessDev from "@/pages/BusinessDev";
import AdminPropertyManagers from "@/pages/AdminPropertyManagers";
import ClientPortal from "@/pages/ClientPortal";
import LegacyReport from "@/pages/LegacyReport";
import AdminInspections from "@/pages/AdminInspections";
import AdminFilterMasterList from "@/pages/AdminFilterMasterList";
import ImportReview from "@/pages/ImportReview";
import AdminAssets from "@/pages/AdminAssets";
import UnitDetail from "@/pages/UnitDetail";
import PropertySnapshot from "@/pages/PropertySnapshot";
import LegalRadar from "@/pages/LegalRadar";
import VacancyBulk from "@/pages/VacancyBulk";
import AskRadar from "@/pages/AskRadar";
import AdminCompletedInspections from "@/pages/AdminCompletedInspections";
import AdminPhotoCommand from "@/pages/AdminPhotoCommand";
import AdminParkingLot from "@/pages/AdminParkingLot";
import AdminReviews from "@/pages/AdminReviews";
import TenantReview from "@/pages/TenantReview";
import AdminWalkRadar from "@/pages/AdminWalkRadar";
import WalkRoute from "@/pages/WalkRoute";
import AdminVoiceToJob from "@/pages/AdminVoiceToJob";
import LiveEta from "@/pages/LiveEta";
import TenantInspectionPortal from "@/pages/TenantInspectionPortal";
import RequestRadarPortal from "@/pages/RequestRadarPortal";
import OwnerApprovalPublic from "@/pages/OwnerApprovalPublic";
import AdminAuditWo from "@/pages/AdminAuditWo";
import AdminNoGpsSessions from "@/pages/AdminNoGpsSessions";
import ReceiptInbox from "@/pages/ReceiptInbox";
import AdminCleanupAutoWos from "@/pages/AdminCleanupAutoWos";
import AdminVendorReview from "@/pages/AdminVendorReview";
import AdminPmrBilling from "@/pages/AdminPmrBilling";
import AdminLcvInvoiceDrop from "@/pages/AdminLcvInvoiceDrop";
import PublicLeaseBuilder from "@/pages/PublicLeaseBuilder";
import LandlordFaq from "@/pages/LandlordFaq";
import TenantPortalPreview from "@/pages/TenantPortalPreview";
import NotifyAck from "@/pages/NotifyAck";
import NotifyResolve from "@/pages/NotifyResolve";
import { AlarmAddendumApprove, AlarmAddendumDecline } from "@/pages/AlarmAddendumDecision";
import AdminSeasonal from "@/pages/AdminSeasonal";
import AdminOfficeInbox from "@/pages/AdminOfficeInbox";
import AdminMoveRadar from "@/pages/AdminMoveRadar";
import AdminClickUp from "@/pages/AdminClickUp";
import AdminReadyToBill from "@/pages/AdminReadyToBill";
import AdminReceiptsPurgatory from "@/pages/AdminReceiptsPurgatory";
import AdminEmergencyDispatch from "@/pages/AdminEmergencyDispatch";
import SmsConsent from "@/pages/SmsConsent";
import FindingsApprovalPublic from "@/pages/FindingsApprovalPublic";
import FounderGate from "@/components/FounderGate";
import AdminAccessLog from "@/pages/AdminAccessLog";
import AdminQrPoster from "@/pages/AdminQrPoster";
import AdminTenantQR from "@/pages/AdminTenantQR";
import AdminMaintainX from "@/pages/AdminMaintainX";
import AdminQuickReport from "@/pages/AdminQuickReport";
import AdminWhatsAppImport from "@/pages/AdminWhatsAppImport";
import AdminBulkPhotos from "@/pages/AdminBulkPhotos";
import AdminKraaftImport from "@/pages/AdminKraaftImport";
import AdminInboxSetup from "@/pages/AdminInboxSetup";
import AdminPropertyInspections from "@/pages/AdminPropertyInspections";
import InspectDetail from "@/pages/InspectDetail";
import InspectionCompare from "@/pages/InspectionCompare";
import TenantInspection from "@/pages/TenantInspection";
import AdminLeftBehind from "@/pages/AdminLeftBehind";
import AdminAISpend from "@/pages/AdminAISpend";
import AdminVendorBills from "@/pages/AdminVendorBills";
import TruckStock from "@/pages/TruckStock";
import AdminTruckStockRoster from "@/pages/AdminTruckStockRoster";
import AdminBacklog from "@/pages/AdminBacklog";
import PropertyFingerprint from "@/pages/PropertyFingerprint";
import PropertyBoard from "@/pages/PropertyBoard";
import VendorComplianceVault from "@/pages/VendorComplianceVault";
import WarrantyTracker from "@/pages/WarrantyTracker";
import LeaseRenewals from "@/pages/LeaseRenewals";
import EmergencyProtocol from "@/pages/EmergencyProtocol";
import DepositReconciliations from "@/pages/DepositReconciliations";
import DepositSign from "@/pages/DepositSign";
import InspectionPrep from "@/pages/InspectionPrep";
import LandlordOnboarding from "@/pages/LandlordOnboarding";
import LandlordOnboardPublic from "@/pages/LandlordOnboardPublic";
import OfficeAdmin from "@/pages/OfficeAdmin";
import TeamCalendar from "@/pages/TeamCalendar";
import Evictions from "@/pages/Evictions";
import DelinquencyRadar from "@/pages/DelinquencyRadar";
import TenantPay from "@/pages/TenantPay";
import VoiceReception from "@/pages/VoiceReception";
import EmergencyMap from "@/pages/EmergencyMap";
import PublicEmergencyMap from "@/pages/PublicEmergencyMap";
import JobPhotoRequest from "@/pages/JobPhotoRequest";
import OfficeAIHelpPage from "@/pages/OfficeAIHelpPage";
import UtilityBillRadar from "@/pages/UtilityBillRadar";
import CompRentRadar from "@/pages/CompRentRadar";
import CORadar from "@/pages/CORadar";
import AdminKraaft from "@/pages/AdminKraaft";
import PublicWoStatus from "@/pages/PublicWoStatus";
import AdminAppfolioImport from "@/pages/AdminAppfolioImport";
import AdminGrokImport from "@/pages/AdminGrokImport";
import AdminInspectionTemplates from "@/pages/AdminInspectionTemplates";
import AdminNeedsDocsBatch from "@/pages/AdminNeedsDocsBatch";
import AdminSmsPhotos from "@/pages/AdminSmsPhotos";
import AdminESign from "@/pages/AdminESign";
import AdminImportWO from "@/pages/AdminImportWO";
import AdminBooking from "@/pages/AdminBooking";
import AdminPitch from "@/pages/AdminPitch";
import ChewHubReport from "@/pages/ChewHubReport";
import ChewHubStatus from "@/pages/ChewHubStatus";
import PermitPortal from "@/pages/PermitPortal";
import WOPermitPortal from "@/pages/WOPermitPortal";
import LeasingAgentPortal from "@/pages/LeasingAgentPortal";
import AdminLeasingAgents from "@/pages/AdminLeasingAgents";
import AdminIntegrations from "@/pages/AdminIntegrations";
import TerminologyAdmin from "@/pages/TerminologyAdmin";
import TimeAdjustAudit from "@/pages/TimeAdjustAudit";
import PartsQueue from "@/pages/PartsQueue";
import RefrigerantLog from "@/pages/RefrigerantLog";
import WeatherDispatch from "@/pages/WeatherDispatch";
import BridgeInbox from "@/pages/BridgeInbox";
import PesticideLog from "@/pages/PesticideLog";
import RouteRadar from "@/pages/RouteRadar";
import PartsReorderList from "@/pages/PartsReorderList";
import HvacUnits from "@/pages/HvacUnits";
import RoofingJobs from "@/pages/RoofingJobs";
import HomeHealthClients from "@/pages/HomeHealthClients";
import LandingWedge from "@/pages/LandingWedge";
import VerticalStorefront from "@/pages/VerticalStorefront";
import { LandlordRentals, LandlordVendors, LandlordRepairs } from "@/pages/LandlordAutopilot";
import { LandlordIntake, LandlordLeaseBuilder, RenterLeaseSign, LandlordApplications, RenterApplication } from "@/pages/LandlordIntakeLease";
import { LegalHealthOverview, RentalComplianceUpload } from "@/pages/landlord/ComplianceLayer";
import RenterPortal from "@/pages/RenterPortal";
import { IndustryProvider, useIndustry } from "@/context/IndustryContext";
import LandlordDashboard from "@/pages/LandlordDashboard";
import LandlordSetupWizard from "@/pages/LandlordSetupWizard";
import LandscapingDashboard from "@/pages/LandscapingDashboard";
import PlumbingDiagnose from "@/pages/PlumbingDiagnose";
import PlumbingDashboard from "@/pages/PlumbingDashboard";
import ElectricalDashboard from "@/pages/ElectricalDashboard";
import HandymanDashboard from "@/pages/HandymanDashboard";
import VendorsDashboard from "@/pages/VendorsDashboard";
import HvacDashboard from "@/pages/HvacDashboard";
import HomeHealthDashboard from "@/pages/HomeHealthDashboard";
import PestDashboard from "@/pages/PestDashboard";
import ElectricalLoadCalc from "@/pages/ElectricalLoadCalc";
import {
  RadarsHub, DailyToolsHub, TruckInventoryHub, DocumentsHub,
  PortfolioHub, TeamHub, AdminHub, OperationsHub, ReportsHub,
  MajorPortfoliosHub,
} from "@/pages/HubPages";
import EmailSyncSetup from "@/pages/EmailSyncSetup";
import AdminChewTickets from "@/pages/AdminChewTickets";
import AdminChewPunchlist from "@/pages/AdminChewPunchlist";
import AdminChewHub from "@/pages/AdminChewHub";
import AdminTenantBridge from "@/pages/AdminTenantBridge";
import TenantBridgeView from "@/pages/TenantBridgeView";
import StudentPortal from "@/pages/StudentPortal";
import AdminPortfolioWorkspace from "@/pages/AdminPortfolioWorkspace";
import AdminPortfolioDedupe from "@/pages/AdminPortfolioDedupe";
import AdminJobsBoard from "@/pages/AdminJobsBoard";
import AdminClientPages from "@/pages/AdminClientPages";
import AdminClientPageDetail from "@/pages/AdminClientPageDetail";
import AdminWOPaste from "@/pages/AdminWOPaste";
import AdminStatusReply from "@/pages/AdminStatusReply";
import ProofGallery from "@/pages/ProofGallery";
import ActivityReportPublic from "@/pages/ActivityReportPublic";
import AdminPhotoBundle from "@/pages/AdminPhotoBundle";
import ClientPortalPublic from "@/pages/ClientPortalPublic";
import PublicPhotoAlbum from "@/pages/PublicPhotoAlbum";
import PublicBooking from "@/pages/PublicBooking";
import MagicLogin from "@/pages/MagicLogin";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { OfficeAIHelpFloating } from "@/components/OfficeAIHelp";
import { ProtectedRoute, SuperAdminRoute } from "@/components/Layout";

// Route switch: `/worker/jobs/:id` → v4 by default; `?v=classic` falls back
// to the legacy JobDetail.jsx during rollout. This lets us ship the new UI
// without deleting anything until the user approves the switch.
function WorkerJobSwitch() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  if (params.get("v") === "classic") return <JobDetail/>;
  return <WorkerJobViewV4/>;
}

// Same feature-flag pattern for /admin/lineup: v2 whiteboard by default,
// classic 3-step wizard on `?v=classic`.
function AdminLineupSwitch() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  if (params.get("v") === "classic") return <AdminLineup/>;
  return <LineupV2/>;
}

// Admin job detail: V2 is now the default (CP-style redesign).
//   - `?v=classic` → force V1 (2-col AdminJobDesktop on wide, AdminJobDetail on narrow)
//   - `?v=v2`      → force V2 (also clears any sticky classic pref)
//   - default      → V2 (AdminJobDetailV2)
function AdminJobSwitch() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const [isNarrow, setIsNarrow] = useState(typeof window !== "undefined" && window.innerWidth < 1024);
  useEffect(() => {
    const on = () => setIsNarrow(window.innerWidth < 1024);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  const urlV = params.get("v");
  if (urlV === "v2") {
    try { localStorage.setItem("fr_admin_dashboard_version", "v2"); } catch {}
    return <AdminJobDetailV2/>;
  }
  if (urlV === "classic") {
    try { localStorage.setItem("fr_admin_dashboard_version", "classic"); } catch {}
    return isNarrow ? <AdminJobDetail/> : <AdminJobDesktop/>;
  }
  let sticky = null;
  try { sticky = localStorage.getItem("fr_admin_dashboard_version"); } catch {}
  if (sticky === "classic") return isNarrow ? <AdminJobDetail/> : <AdminJobDesktop/>;
  return <AdminJobDetailV2/>;
}

// Admin jobs list: V2 (kanban/list) is now the default. `?v=classic` opts out.
function AdminJobsSwitch() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const urlV = params.get("v");
  if (urlV === "v2") {
    try { localStorage.setItem("fr_admin_dashboard_version", "v2"); } catch {}
    return <AdminJobsV2/>;
  }
  if (urlV === "classic") {
    try { localStorage.setItem("fr_admin_dashboard_version", "classic"); } catch {}
    return <AdminJobs/>;
  }
  let sticky = null;
  try { sticky = localStorage.getItem("fr_admin_dashboard_version"); } catch {}
  if (sticky === "classic") return <AdminJobs/>;
  return <AdminJobsV2/>;
}

// Property detail: V2 (branded hero) is now the default. `?v=classic` opts out.
function PropertyDetailSwitch() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const urlV = params.get("v");
  if (urlV === "v2") {
    try { localStorage.setItem("fr_admin_dashboard_version", "v2"); } catch {}
    return <PropertyDetailV2/>;
  }
  if (urlV === "classic") {
    try { localStorage.setItem("fr_admin_dashboard_version", "classic"); } catch {}
    return <PropertyDetail/>;
  }
  let sticky = null;
  try { sticky = localStorage.getItem("fr_admin_dashboard_version"); } catch {}
  if (sticky === "classic") return <PropertyDetail/>;
  return <PropertyDetailV2/>;
}

// Worker dashboard: v2 phone-first landing by default, classic on `?v=classic`.
function WorkerDashboardSwitch() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  if (params.get("v") === "classic") return <WorkerDashboard/>;
  return <WorkerDashboardV2/>;
}

function AdminHome() {
  const ind = useIndustry();
  const [setupStatus, setSetupStatus] = useState(null); // null=loading, {}=loaded
  useEffect(() => {
    if (ind?.industry !== "landlord") { setSetupStatus({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const { api } = await import("@/lib/api");
        const r = await api.get("/landlord-auto/setup/status");
        if (!cancelled) setSetupStatus(r.data);
      } catch { if (!cancelled) setSetupStatus({}); }
    })();
    return () => { cancelled = true; };
  }, [ind?.industry]);
  if (ind?.industry === "landlord") {
    if (setupStatus === null) return null; // block flash while checking
    // Only redirect to wizard for truly-fresh tenants — existing landlords
    // with rentals shouldn't be forced back through onboarding.
    const needsWizard = setupStatus.completed === false && !setupStatus.has_rentals;
    if (needsWizard) return <Navigate to="/admin/landlord/setup" replace/>;
    return <LandlordDashboard/>;
  }
  if (ind?.industry === "landscaping") return <LandscapingDashboard/>;
  if (ind?.industry === "plumbing") return <PlumbingDashboard/>;
  if (ind?.industry === "electrical") return <ElectricalDashboard/>;
  if (ind?.industry === "handyman") return <HandymanDashboard/>;
  if (ind?.industry === "vendors") return <VendorsDashboard/>;
  if (ind?.industry === "hvac") return <HvacDashboard/>;
  if (ind?.industry === "home_health") return <HomeHealthDashboard/>;
  if (ind?.industry === "pest_control") return <PestDashboard/>;
  return <AdminDashboardSwitch/>;
}

// Turns Board: V2 (CP-style hero + KPI cards) is now default. `?v=classic` opts out.
function AdminTurnsBoardSwitch() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const urlV = params.get("v");
  if (urlV === "v2") {
    try { localStorage.setItem("fr_admin_dashboard_version", "v2"); } catch {}
    return <AdminTurnsBoardV2/>;
  }
  if (urlV === "classic") {
    try { localStorage.setItem("fr_admin_dashboard_version", "classic"); } catch {}
    return <AdminTurnsBoard/>;
  }
  let sticky = null;
  try { sticky = localStorage.getItem("fr_admin_dashboard_version"); } catch {}
  if (sticky === "classic") return <AdminTurnsBoard/>;
  return <AdminTurnsBoardV2/>;
}

// Admin (PM) dashboard version switch.
// - V2 is now the DEFAULT (CP-style redesign)
// - `?v=classic` → force V1 (also stores sticky "classic" pref)
// - `?v=v2` → force V2 (clears any classic sticky pref)
function AdminDashboardSwitch() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const urlV = params.get("v");
  if (urlV === "v2") {
    try { localStorage.setItem("fr_admin_dashboard_version", "v2"); } catch {}
    return <AdminDashboardV2/>;
  }
  if (urlV === "classic") {
    try { localStorage.setItem("fr_admin_dashboard_version", "classic"); } catch {}
    return <AdminDashboard/>;
  }
  let sticky = null;
  try { sticky = localStorage.getItem("fr_admin_dashboard_version"); } catch {}
  if (sticky === "classic") return <AdminDashboard/>;
  return <AdminDashboardV2/>;
}

function BoundaryWithLocation({ children }) {
  const loc = useLocation();
  return <RouteErrorBoundary location={loc.pathname}>{children}</RouteErrorBoundary>;
}

function App() {
  return (
    <AuthProvider>
      <TenantProvider>
        <IndustryProvider>
          <VendorFilterProvider>
          <BrowserRouter>
          <Toaster position="top-right" richColors closeButton/>
          <ImpersonationBanner/>
          <CommandPalette/>
          <BoundaryWithLocation>
          <Routes>
            <Route path="/" element={<Landing/>}/>
            <Route path="/trust" element={<Trust/>}/>
            <Route path="/public/photos/:token" element={<PublicPhotos/>}/>
            <Route path="/public/bid/:token" element={<PublicBid/>}/>
            <Route path="/public/inspection/:token" element={<PublicInspectionReport/>}/>
            <Route path="/admin/inspections/:iid" element={<ProtectedRoute role="admin"><PropertyInspection/></ProtectedRoute>}/>
            <Route path="/login" element={<Login/>}/>
            <Route path="/signup" element={<Signup/>}/>
            <Route path="/opt-in-policy" element={<OptInPolicy/>}/>
            <Route path="/pest/signup" element={<Signup lockedIndustry="pest_control"/>}/>
            <Route path="/hvac/signup" element={<Signup lockedIndustry="hvac"/>}/>
            <Route path="/roofing/signup" element={<Signup lockedIndustry="roofing"/>}/>
            <Route path="/home-care/signup" element={<Signup lockedIndustry="home_health"/>}/>
            <Route path="/landlord/signup" element={<Signup lockedIndustry="landlord"/>}/>
            <Route path="/landscaping/signup" element={<Signup lockedIndustry="landscaping"/>}/>
            <Route path="/plumbing/signup" element={<Signup lockedIndustry="plumbing"/>}/>
            <Route path="/electrical/signup" element={<Signup lockedIndustry="electrical"/>}/>
            <Route path="/handyman/signup" element={<Signup lockedIndustry="handyman"/>}/>
            <Route path="/vendors/signup" element={<Signup lockedIndustry="vendors"/>}/>

            <Route path="/super-admin" element={<SuperAdminRoute><SuperAdmin/></SuperAdminRoute>}/>
            <Route path="/brand/motion" element={<BrandMotionMock/>}/>
            <Route path="/admin/demos" element={<ProtectedRoute role="admin"><DemosHub/></ProtectedRoute>}/>

            <Route path="/worker" element={<ProtectedRoute><WorkerDashboardSwitch/></ProtectedRoute>}/>
            <Route path="/worker/jobs/:id" element={<ProtectedRoute><WorkerJobSwitch/></ProtectedRoute>}/>

            <Route path="/admin" element={<ProtectedRoute role="admin"><AdminHome/></ProtectedRoute>}/>
            <Route path="/admin/design-preview" element={<ProtectedRoute role="admin"><DesignPreview/></ProtectedRoute>}/>
            <Route path="/admin/design-preview/property" element={<ProtectedRoute role="admin"><PropertyDetailPreview/></ProtectedRoute>}/>
            <Route path="/admin/design-preview/jobs" element={<ProtectedRoute role="admin"><WorkOrdersPreview/></ProtectedRoute>}/>
            <Route path="/admin/design-preview/wo" element={<ProtectedRoute role="admin"><WorkOrderDetailPreview/></ProtectedRoute>}/>
            <Route path="/admin/design-preview/*" element={<ProtectedRoute role="admin"><DesignPreviewStub/></ProtectedRoute>}/>
            <Route path="/admin/jobs" element={<ProtectedRoute role="admin"><AdminJobsSwitch/></ProtectedRoute>}/>
            <Route path="/admin/photo-search" element={<ProtectedRoute role="admin"><AdminPhotoSearch/></ProtectedRoute>}/>
            <Route path="/admin/needs-docs/batch" element={<ProtectedRoute role="admin"><AdminNeedsDocsBatch/></ProtectedRoute>}/>
            <Route path="/admin/settings/sms-photos" element={<ProtectedRoute role="admin"><AdminSmsPhotos/></ProtectedRoute>}/>
            <Route path="/admin/csv-sync-history" element={<ProtectedRoute role="admin"><AdminCsvSyncHistory/></ProtectedRoute>}/>
            <Route path="/admin/jobs/:id" element={<ProtectedRoute role="admin"><AdminJobSwitch/></ProtectedRoute>}/>
            <Route path="/admin/jobs/:id/legal-protocol" element={<ProtectedRoute role="admin"><AdminJobLegalProtocol/></ProtectedRoute>}/>
            <Route path="/admin/duplicates" element={<ProtectedRoute role="admin"><AdminDuplicates/></ProtectedRoute>}/>
            <Route path="/admin/portfolio-merge" element={<ProtectedRoute role="admin"><AdminPortfolioMergePreview/></ProtectedRoute>}/>
            <Route path="/admin/jobs-dedupe" element={<ProtectedRoute role="admin"><AdminJobsDedupe/></ProtectedRoute>}/>
            <Route path="/admin/esign" element={<ProtectedRoute role="admin"><AdminESign/></ProtectedRoute>}/>
            <Route path="/admin/import-wo" element={<ProtectedRoute role="admin"><AdminImportWO/></ProtectedRoute>}/>
            <Route path="/admin/safety-board" element={<ProtectedRoute role="admin"><SafetyBoard/></ProtectedRoute>}/>
            <Route path="/admin/turns" element={<ProtectedRoute role="admin"><TurnDashboard/></ProtectedRoute>}/>
            <Route path="/admin/turns-board" element={<ProtectedRoute role="admin"><AdminTurnsBoardSwitch/></ProtectedRoute>}/>
            <Route path="/admin/purge/truck-sheet" element={<ProtectedRoute role="admin"><AdminPurgeTruckSheet/></ProtectedRoute>}/>
            <Route path="/admin/live-feed" element={<ProtectedRoute role="admin"><AdminLiveFeed/></ProtectedRoute>}/>
            <Route path="/admin/appliance-iq" element={<ProtectedRoute role="admin"><AdminApplianceIQ/></ProtectedRoute>}/>
            <Route path="/tech/turns" element={<ProtectedRoute><TechTurns/></ProtectedRoute>}/>
            <Route path="/admin/inspections" element={<ProtectedRoute role="admin"><InspectionsBoard/></ProtectedRoute>}/>
            <Route path="/admin/import" element={<ProtectedRoute role="admin"><ImportReview/></ProtectedRoute>}/>
            <Route path="/admin/inbox" element={<ProtectedRoute role="admin"><AdminInbox/></ProtectedRoute>}/>
            <Route path="/admin/team-chat" element={<ProtectedRoute role="admin"><TeamChat/></ProtectedRoute>}/>
            <Route path="/worker/team-chat" element={<ProtectedRoute role="worker"><TeamChat/></ProtectedRoute>}/>
            <Route path="/admin/properties" element={<ProtectedRoute role="admin"><PropertiesList/></ProtectedRoute>}/>
            <Route path="/admin/radar/legal" element={<ProtectedRoute role="admin"><LegalRadar/></ProtectedRoute>}/>
            <Route path="/admin/properties/:address" element={<ProtectedRoute role="admin"><PropertyDetailSwitch/></ProtectedRoute>}/>
            <Route path="/admin/properties/:address/walk" element={<ProtectedRoute role="admin"><PropertyWalk/></ProtectedRoute>}/>
            <Route path="/admin/properties/:address/unit/:unit" element={<ProtectedRoute role="admin"><UnitDetail/></ProtectedRoute>}/>
            <Route path="/admin/properties/:address/snapshot" element={<ProtectedRoute role="admin"><PropertySnapshot/></ProtectedRoute>}/>
            <Route path="/admin/properties/:address/vacancy-bulk" element={<ProtectedRoute role="admin"><VacancyBulk/></ProtectedRoute>}/>
            <Route path="/admin/workers" element={<ProtectedRoute role="admin"><AdminWorkers/></ProtectedRoute>}/>
            <Route path="/admin/sub-contractors" element={<ProtectedRoute role="admin"><AdminSubContractors/></ProtectedRoute>}/>
            <Route path="/admin/lineup" element={<ProtectedRoute role="admin"><AdminLineupSwitch/></ProtectedRoute>}/>
            <Route path="/admin/schedule" element={<ProtectedRoute role="admin"><AdminScheduleWeek/></ProtectedRoute>}/>
            <Route path="/admin/schedule/month" element={<ProtectedRoute role="admin"><AdminScheduleMonth/></ProtectedRoute>}/>
            <Route path="/sub/:token" element={<SubPortal/>}/>
            <Route path="/portal/:token" element={<ClientPortal/>}/>
            <Route path="/tenant-inspection/:token" element={<TenantInspectionPortal/>}/>
            <Route path="/report/:token" element={<RequestRadarPortal/>}/>
            <Route path="/owner-approval/:token" element={<OwnerApprovalPublic/>}/>
            <Route path="/album/:token" element={<PublicPhotoAlbum/>}/>
            <Route path="/book/:slug" element={<PublicBooking/>}/>
            <Route path="/book/:slug/:mtype" element={<PublicBooking/>}/>
            <Route path="/admin/booking" element={<ProtectedRoute role="admin"><AdminBooking/></ProtectedRoute>}/>
            <Route path="/admin/pitch" element={<ProtectedRoute role="admin"><AdminPitch/></ProtectedRoute>}/>
            <Route path="/tenant/login" element={<TenantLogin/>}/>
            <Route path="/landlord/login" element={<LandlordLogin/>}/>
            <Route path="/tenant-portal-preview" element={<TenantPortalPreview/>}/>
            <Route path="/notify/ack/:token" element={<NotifyAck/>}/>
            <Route path="/notify/resolve/:token" element={<NotifyResolve/>}/>
            <Route path="/alarm-addendum/approve/:token" element={<AlarmAddendumApprove/>}/>
            <Route path="/alarm-addendum/decline/:token" element={<AlarmAddendumDecline/>}/>
            <Route path="/admin/landlords" element={<ProtectedRoute role="admin"><AdminLandlords/></ProtectedRoute>}/>
            <Route path="/admin/landlord/:id" element={<ProtectedRoute role="admin"><AdminLandlordDetail/></ProtectedRoute>}/>
            <Route path="/admin/drafts" element={<ProtectedRoute role="admin"><DraftsQueue/></ProtectedRoute>}/>
            <Route path="/admin/insurance" element={<ProtectedRoute role="admin"><AdminInsurance/></ProtectedRoute>}/>
            <Route path="/admin/philly-radar" element={<ProtectedRoute role="admin"><PhillyComplianceRadar/></ProtectedRoute>}/>
            <Route path="/admin/business-dev" element={<ProtectedRoute role="admin"><BusinessDev/></ProtectedRoute>}/>
            <Route path="/admin/property-managers" element={<ProtectedRoute role="admin"><AdminPropertyManagers/></ProtectedRoute>}/>
            <Route path="/admin/tenant-inspections" element={<ProtectedRoute role="admin"><AdminInspections/></ProtectedRoute>}/>
            <Route path="/admin/assets" element={<ProtectedRoute role="admin"><AdminAssets/></ProtectedRoute>}/>
            <Route path="/admin/completed-inspections" element={<ProtectedRoute role="admin"><AdminCompletedInspections/></ProtectedRoute>}/>
            <Route path="/admin/filter-master-list" element={<ProtectedRoute role="admin"><AdminFilterMasterList/></ProtectedRoute>}/>
            <Route path="/admin/photo-command" element={<ProtectedRoute role="admin"><AdminPhotoCommand/></ProtectedRoute>}/>
            <Route path="/admin/ask-radar" element={<ProtectedRoute role="admin"><AskRadar/></ProtectedRoute>}/>
            <Route path="/admin/parking-lot" element={<ProtectedRoute role="admin"><AdminParkingLot/></ProtectedRoute>}/>
            <Route path="/admin/reviews" element={<ProtectedRoute role="admin"><AdminReviews/></ProtectedRoute>}/>
            <Route path="/review/:token" element={<TenantReview/>}/>
            <Route path="/admin/walk-radar" element={<ProtectedRoute role="admin"><AdminWalkRadar/></ProtectedRoute>}/>
            <Route path="/walk/:id" element={<ProtectedRoute><WalkRoute/></ProtectedRoute>}/>
            <Route path="/admin/voice-to-job" element={<ProtectedRoute role="admin"><AdminVoiceToJob/></ProtectedRoute>}/>
            <Route path="/eta/:token" element={<LiveEta/>}/>
            <Route path="/admin/map" element={<ProtectedRoute role="admin"><MapView/></ProtectedRoute>}/>
            <Route path="/admin/live" element={<ProtectedRoute role="admin"><LiveField/></ProtectedRoute>}/>
            <Route path="/admin/reports" element={<ProtectedRoute role="admin"><AdminReports/></ProtectedRoute>}/>
            <Route path="/admin/settings" element={<ProtectedRoute role="admin"><Settings/></ProtectedRoute>}/>
            <Route path="/admin/audit-wo" element={<ProtectedRoute role="admin"><AdminAuditWo/></ProtectedRoute>}/>
            <Route path="/admin/no-gps-sessions" element={<ProtectedRoute role="admin"><AdminNoGpsSessions/></ProtectedRoute>}/>
            <Route path="/admin/receipts/inbox" element={<ProtectedRoute role="admin"><ReceiptInbox/></ProtectedRoute>}/>
            <Route path="/admin/time-audit" element={<ProtectedRoute role="admin"><TimeAdjustAudit/></ProtectedRoute>}/>
            <Route path="/admin/parts-queue" element={<ProtectedRoute><PartsQueue/></ProtectedRoute>}/>
            <Route path="/parts-queue" element={<ProtectedRoute><PartsQueue/></ProtectedRoute>}/>
            <Route path="/admin/cleanup-auto-wos" element={<ProtectedRoute role="admin"><AdminCleanupAutoWos/></ProtectedRoute>}/>
            <Route path="/admin/vendor-review" element={<ProtectedRoute role="admin"><AdminVendorReview/></ProtectedRoute>}/>
            <Route path="/admin/pmr-billing" element={<ProtectedRoute role="admin"><AdminPmrBilling/></ProtectedRoute>}/>
            <Route path="/admin/lcv-invoice-drop" element={<ProtectedRoute role="admin"><AdminLcvInvoiceDrop/></ProtectedRoute>}/>
            <Route path="/admin/billing" element={<ProtectedRoute role="admin"><FounderGate area="billing"><BillingSettings/></FounderGate></ProtectedRoute>}/>
            <Route path="/admin/prospects" element={<ProtectedRoute role="admin"><ProspectRadar/></ProtectedRoute>}/>
            <Route path="/apply/:token" element={<ApplicationPublic/>}/>
            <Route path="/admin/vacancies" element={<ProtectedRoute role="admin"><VacancyRadar/></ProtectedRoute>}/>
            <Route path="/admin/vacant-security" element={<ProtectedRoute><VacantSecurity/></ProtectedRoute>}/>
            <Route path="/worker/vacant-security" element={<ProtectedRoute><VacantSecurity/></ProtectedRoute>}/>
            <Route path="/admin/rentcast-preview" element={<ProtectedRoute role="admin"><RentcastPreview/></ProtectedRoute>}/>
            <Route path="/admin/turn-kits" element={<ProtectedRoute role="admin"><TurnKits/></ProtectedRoute>}/>
            <Route path="/admin/projects" element={<ProtectedRoute role="admin"><ProjectRadar/></ProtectedRoute>}/>
            <Route path="/admin/projects/:id" element={<ProtectedRoute role="admin"><ProjectRadarDetail/></ProtectedRoute>}/>
            <Route path="/project/:token" element={<PublicProjectView/>}/>
            <Route path="/project/:token/unit/:unit" element={<PublicProjectView/>}/>
            <Route path="/tradesman/:token" element={<TradesmanPortal/>}/>
            <Route path="/cleaner/:token" element={<TradesmanPortal/>}/>
            <Route path="/tradesman-signed-out" element={<TradesmanSignedOut/>}/>
            <Route path="/chew/student/:token" element={<ChewStudentPortal/>}/>
            <Route path="/t/:token" element={<TenantBridgeView/>}/>
            <Route path="/report/chew-flats" element={<ChewHubReport/>}/>
            <Route path="/report/chew-flats/status/:ticketId" element={<ChewHubStatus/>}/>
            <Route path="/permit/:token" element={<PermitPortal/>}/>
            <Route path="/wo-permit/:token" element={<WOPermitPortal/>}/>
            <Route path="/leasing/:token" element={<LeasingAgentPortal/>}/>
            <Route path="/admin/leasing" element={<ProtectedRoute role="admin"><AdminLeasingAgents/></ProtectedRoute>}/>
            <Route path="/admin/integrations" element={<ProtectedRoute role="admin"><AdminIntegrations/></ProtectedRoute>}/>
            <Route path="/admin/terminology" element={<ProtectedRoute role="admin"><TerminologyAdmin/></ProtectedRoute>}/>
            <Route path="/admin/hvac/refrigerant-log" element={<ProtectedRoute role="admin"><RefrigerantLog/></ProtectedRoute>}/>
            <Route path="/admin/roofing/weather-dispatch" element={<ProtectedRoute role="admin"><WeatherDispatch/></ProtectedRoute>}/>
            <Route path="/admin/bridge-inbox" element={<ProtectedRoute role="admin"><BridgeInbox/></ProtectedRoute>}/>
            <Route path="/admin/pest/pesticide-log" element={<ProtectedRoute role="admin"><PesticideLog/></ProtectedRoute>}/>
            <Route path="/admin/pest/route-radar" element={<ProtectedRoute role="admin"><RouteRadar/></ProtectedRoute>}/>
            <Route path="/admin/hvac/units" element={<ProtectedRoute role="admin"><HvacUnits/></ProtectedRoute>}/>
            <Route path="/admin/parts-reorder" element={<ProtectedRoute role="admin"><PartsReorderList/></ProtectedRoute>}/>
            <Route path="/admin/roofing/jobs" element={<ProtectedRoute role="admin"><RoofingJobs/></ProtectedRoute>}/>
            <Route path="/admin/home-health/clients" element={<ProtectedRoute role="admin"><HomeHealthClients/></ProtectedRoute>}/>
            <Route path="/for-property-managers" element={<LandingWedge wedgeKey="pm"/>}/>
            <Route path="/handymen" element={<LandingWedge wedgeKey="handyman"/>}/>
            <Route path="/hvac" element={<VerticalStorefront storefrontKey="hvac"/>}/>
            <Route path="/pest" element={<VerticalStorefront storefrontKey="pest_control"/>}/>
            <Route path="/roofing" element={<VerticalStorefront storefrontKey="roofing"/>}/>
            <Route path="/home-care" element={<VerticalStorefront storefrontKey="home_health"/>}/>
            <Route path="/landlord" element={<VerticalStorefront storefrontKey="landlord"/>}/>
            <Route path="/lease-builder" element={<PublicLeaseBuilder/>}/>
            <Route path="/landlord/faq" element={<LandlordFaq/>}/>
            <Route path="/landscaping" element={<VerticalStorefront storefrontKey="landscaping"/>}/>
            <Route path="/plumbing" element={<VerticalStorefront storefrontKey="plumbing"/>}/>
            <Route path="/electrical" element={<VerticalStorefront storefrontKey="electrical"/>}/>
            <Route path="/handyman" element={<VerticalStorefront storefrontKey="handyman"/>}/>
            <Route path="/vendors" element={<VerticalStorefront storefrontKey="vendors"/>}/>
            <Route path="/admin/landscaping" element={<ProtectedRoute role="admin"><LandscapingDashboard/></ProtectedRoute>}/>
            <Route path="/admin/landlord/setup" element={<ProtectedRoute role="admin"><LandlordSetupWizard/></ProtectedRoute>}/>
            <Route path="/admin/plumbing" element={<ProtectedRoute role="admin"><PlumbingDashboard/></ProtectedRoute>}/>
            <Route path="/admin/electrical" element={<ProtectedRoute role="admin"><ElectricalDashboard/></ProtectedRoute>}/>
            <Route path="/admin/handyman" element={<ProtectedRoute role="admin"><HandymanDashboard/></ProtectedRoute>}/>
            <Route path="/admin/vendors" element={<ProtectedRoute role="admin"><VendorsDashboard/></ProtectedRoute>}/>
            <Route path="/admin/hvac" element={<ProtectedRoute role="admin"><HvacDashboard/></ProtectedRoute>}/>
            <Route path="/admin/home-health" element={<ProtectedRoute role="admin"><HomeHealthDashboard/></ProtectedRoute>}/>
            <Route path="/admin/pest" element={<ProtectedRoute role="admin"><PestDashboard/></ProtectedRoute>}/>
            {/* Sidebar-group index hubs — click a group header to land here */}
            <Route path="/admin/radars" element={<ProtectedRoute role="admin"><RadarsHub/></ProtectedRoute>}/>
            <Route path="/admin/daily-tools" element={<ProtectedRoute role="admin"><DailyToolsHub/></ProtectedRoute>}/>
            <Route path="/admin/truck-inventory" element={<ProtectedRoute role="admin"><TruckInventoryHub/></ProtectedRoute>}/>
            <Route path="/admin/documents" element={<ProtectedRoute role="admin"><DocumentsHub/></ProtectedRoute>}/>
            <Route path="/admin/portfolio-hub" element={<ProtectedRoute role="admin"><PortfolioHub/></ProtectedRoute>}/>
            <Route path="/admin/team-hub" element={<ProtectedRoute role="admin"><TeamHub/></ProtectedRoute>}/>
            <Route path="/admin/admin-hub" element={<ProtectedRoute role="admin"><AdminHub/></ProtectedRoute>}/>
            <Route path="/admin/operations-hub" element={<ProtectedRoute role="admin"><OperationsHub/></ProtectedRoute>}/>
            <Route path="/admin/reports-hub" element={<ProtectedRoute role="admin"><ReportsHub/></ProtectedRoute>}/>
            <Route path="/admin/major-portfolios-hub" element={<ProtectedRoute role="admin"><MajorPortfoliosHub/></ProtectedRoute>}/>
            <Route path="/admin/email-sync-setup" element={<ProtectedRoute role="admin"><EmailSyncSetup/></ProtectedRoute>}/>
            <Route path="/admin/plumbing/leak-diagnose" element={<ProtectedRoute role="admin"><PlumbingDiagnose/></ProtectedRoute>}/>
            <Route path="/admin/electrical/load-calc" element={<ProtectedRoute role="admin"><ElectricalLoadCalc/></ProtectedRoute>}/>
            <Route path="/rent/:token" element={<RenterPortal/>}/>
            <Route path="/admin/landlord/rentals" element={<ProtectedRoute role="admin"><LandlordRentals/></ProtectedRoute>}/>
            <Route path="/admin/landlord/rentals/:id/intake" element={<ProtectedRoute role="admin"><LandlordIntake/></ProtectedRoute>}/>
            <Route path="/admin/landlord/rentals/:id/lease" element={<ProtectedRoute role="admin"><LandlordLeaseBuilder/></ProtectedRoute>}/>
            <Route path="/admin/landlord/rentals/:id/applications" element={<ProtectedRoute role="admin"><LandlordApplications/></ProtectedRoute>}/>
            <Route path="/admin/landlord/rentals/:id/compliance" element={<ProtectedRoute role="admin"><RentalComplianceUpload/></ProtectedRoute>}/>
            <Route path="/admin/landlord/legal-health" element={<ProtectedRoute role="admin"><LegalHealthOverview/></ProtectedRoute>}/>
            <Route path="/rent-apply/:token" element={<RenterApplication/>}/>
            <Route path="/admin/landlord/vendors" element={<ProtectedRoute role="admin"><LandlordVendors/></ProtectedRoute>}/>
            <Route path="/admin/landlord/repairs" element={<ProtectedRoute role="admin"><LandlordRepairs/></ProtectedRoute>}/>
            <Route path="/rent-lease/:token" element={<RenterLeaseSign/>}/>
            <Route path="/plumbers" element={<LandingWedge wedgeKey="plumbing"/>}/>
            <Route path="/roofers" element={<LandingWedge wedgeKey="roofing"/>}/>
            <Route path="/student" element={<StudentPortal/>}/>
            <Route path="/my-requests" element={<StudentPortal/>}/>
            <Route path="/admin/chew-tickets" element={<ProtectedRoute role="admin"><AdminChewTickets/></ProtectedRoute>}/>
            <Route path="/admin/chew-punchlist" element={<ProtectedRoute role="admin"><AdminChewPunchlist/></ProtectedRoute>}/>
            <Route path="/admin/chew-hub" element={<ProtectedRoute role="admin"><AdminChewHub/></ProtectedRoute>}/>
            <Route path="/admin/tenant-bridge" element={<ProtectedRoute role="admin"><AdminTenantBridge/></ProtectedRoute>}/>
            <Route path="/admin/portfolios/:slug" element={<ProtectedRoute role="admin"><AdminPortfolioWorkspace/></ProtectedRoute>}/>
            <Route path="/admin/portfolio-dedupe" element={<ProtectedRoute role="admin"><AdminPortfolioDedupe/></ProtectedRoute>}/>
            <Route path="/admin/jobs-board" element={<ProtectedRoute role="admin"><AdminJobsBoard/></ProtectedRoute>}/>
            <Route path="/admin/clients" element={<ProtectedRoute role="admin"><AdminClientPages/></ProtectedRoute>}/>
            <Route path="/admin/clients/:id" element={<ProtectedRoute role="admin"><AdminClientPageDetail/></ProtectedRoute>}/>
            <Route path="/admin/wo-paste" element={<ProtectedRoute role="admin"><AdminWOPaste/></ProtectedRoute>}/>
            <Route path="/admin/status-reply" element={<ProtectedRoute role="admin"><AdminStatusReply/></ProtectedRoute>}/>
            <Route path="/admin/photo-bundle" element={<ProtectedRoute role="admin"><AdminPhotoBundle/></ProtectedRoute>}/>
            <Route path="/admin/photo-bundle/:id" element={<ProtectedRoute role="admin"><AdminPhotoBundle/></ProtectedRoute>}/>
            <Route path="/landlord/:token" element={<LandlordPortalView/>}/>
            <Route path="/landlord/:token/inbox" element={<LandlordInbox/>}/>
            <Route path="/landlord/:token/property/:addressKey" element={<LandlordPropertyDetail/>}/>
            <Route path="/quotes/approve/:token" element={<LandlordQuoteApproval/>}/>
            <Route path="/admin/radar/pm" element={<ProtectedRoute role="admin"><PMRadar/></ProtectedRoute>}/>
            <Route path="/admin/owner-statements" element={<ProtectedRoute role="admin"><OwnerStatements/></ProtectedRoute>}/>
            <Route path="/admin/legacy-report" element={<ProtectedRoute role="admin"><LegacyReport/></ProtectedRoute>}/>
            <Route path="/admin/branding" element={<ProtectedRoute role="admin"><FounderGate area="branding"><TenantBranding/></FounderGate></ProtectedRoute>}/>
            <Route path="/admin/activity" element={<ProtectedRoute role="admin"><AdminActivity/></ProtectedRoute>}/>
            <Route path="/admin/cameras" element={<ProtectedRoute role="admin"><AdminCameras/></ProtectedRoute>}/>
            <Route path="/admin/seasonal" element={<ProtectedRoute role="admin"><AdminSeasonal/></ProtectedRoute>}/>
            <Route path="/admin/office-inbox" element={<ProtectedRoute role="admin"><AdminOfficeInbox/></ProtectedRoute>}/>
            <Route path="/admin/move-radar" element={<ProtectedRoute role="admin"><AdminMoveRadar/></ProtectedRoute>}/>
            <Route path="/admin/clickup" element={<ProtectedRoute role="admin"><AdminClickUp/></ProtectedRoute>}/>
            <Route path="/admin/ready-to-bill" element={<ProtectedRoute role="admin"><AdminReadyToBill/></ProtectedRoute>}/>
            <Route path="/admin/receipts" element={<ProtectedRoute role="admin"><AdminReceiptsPurgatory/></ProtectedRoute>}/>
            <Route path="/admin/emergency-dispatch" element={<ProtectedRoute role="admin"><AdminEmergencyDispatch/></ProtectedRoute>}/>
            <Route path="/sms-consent/:slug" element={<SmsConsent/>}/>
            <Route path="/findings-approval/:token" element={<FindingsApprovalPublic/>}/>
            <Route path="/admin/access-log" element={<ProtectedRoute role="admin"><AdminAccessLog/></ProtectedRoute>}/>
            <Route path="/admin/qr-poster" element={<ProtectedRoute role="admin"><AdminQrPoster/></ProtectedRoute>}/>
            <Route path="/admin/tenant-qr" element={<ProtectedRoute role="admin"><AdminTenantQR/></ProtectedRoute>}/>
            <Route path="/admin/maintainx" element={<ProtectedRoute role="admin"><AdminMaintainX/></ProtectedRoute>}/>
            <Route path="/admin/quick-report" element={<ProtectedRoute role="admin"><AdminQuickReport/></ProtectedRoute>}/>
            <Route path="/admin/integrations/clickup" element={<ProtectedRoute role="admin"><AdminClickUp/></ProtectedRoute>}/>
            <Route path="/admin/whatsapp-import" element={<ProtectedRoute role="admin"><AdminWhatsAppImport/></ProtectedRoute>}/>
            <Route path="/admin/kraaft-import" element={<ProtectedRoute role="admin"><AdminKraaftImport/></ProtectedRoute>}/>
            <Route path="/admin/inbox-setup" element={<ProtectedRoute role="admin"><AdminInboxSetup/></ProtectedRoute>}/>
            <Route path="/admin/bulk-photos" element={<ProtectedRoute role="admin"><AdminBulkPhotos/></ProtectedRoute>}/>
            <Route path="/admin/property-inspections" element={<ProtectedRoute role="admin"><AdminPropertyInspections/></ProtectedRoute>}/>
            <Route path="/admin/property-inspections/:id" element={<ProtectedRoute role="admin"><InspectDetail/></ProtectedRoute>}/>
            <Route path="/admin/property-inspections/:id/compare" element={<ProtectedRoute role="admin"><InspectionCompare/></ProtectedRoute>}/>
            <Route path="/inspect/:id" element={<ProtectedRoute><InspectDetail/></ProtectedRoute>}/>
            <Route path="/tenant-inspect/:token" element={<TenantInspection/>}/>
            <Route path="/admin/ops/vendors" element={<ProtectedRoute role="admin"><VendorComplianceVault/></ProtectedRoute>}/>
            <Route path="/admin/ops/warranties" element={<ProtectedRoute role="admin"><WarrantyTracker/></ProtectedRoute>}/>
            <Route path="/admin/ops/lease-renewals" element={<ProtectedRoute role="admin"><LeaseRenewals/></ProtectedRoute>}/>
            <Route path="/admin/ops/emergency" element={<ProtectedRoute role="admin"><EmergencyProtocol/></ProtectedRoute>}/>
            <Route path="/admin/ops/deposits" element={<ProtectedRoute role="admin"><DepositReconciliations/></ProtectedRoute>}/>
            <Route path="/admin/ops/inspection-prep" element={<ProtectedRoute role="admin"><InspectionPrep/></ProtectedRoute>}/>
            <Route path="/admin/onboarding" element={<ProtectedRoute role="admin"><LandlordOnboarding/></ProtectedRoute>}/>
            <Route path="/admin/office" element={<ProtectedRoute role="admin"><OfficeAdmin/></ProtectedRoute>}/>
            <Route path="/admin/calendar" element={<ProtectedRoute role="admin"><TeamCalendar/></ProtectedRoute>}/>
            <Route path="/admin/evictions" element={<ProtectedRoute role="admin"><Evictions/></ProtectedRoute>}/>
            <Route path="/admin/delinquency" element={<ProtectedRoute role="admin"><DelinquencyRadar/></ProtectedRoute>}/>
            <Route path="/admin/voice-reception" element={<ProtectedRoute role="admin"><VoiceReception/></ProtectedRoute>}/>
            <Route path="/admin/emergency-map" element={<ProtectedRoute role="admin"><EmergencyMap/></ProtectedRoute>}/>
            <Route path="/pay/:token" element={<TenantPay/>}/>
            <Route path="/emergency/:token" element={<PublicEmergencyMap/>}/>
            <Route path="/onboard/:token" element={<LandlordOnboardPublic/>}/>
            <Route path="/deposit-sign/:token" element={<DepositSign/>}/>
            <Route path="/j-photos/:token" element={<JobPhotoRequest/>}/>
            <Route path="/admin/ai-help" element={<OfficeAIHelpPage/>}/>
            <Route path="/admin/utility-bills" element={<UtilityBillRadar/>}/>
            <Route path="/admin/comp-rent" element={<CompRentRadar/>}/>
            <Route path="/admin/co-radar" element={<CORadar/>}/>
            <Route path="/admin/left-behind" element={<ProtectedRoute role="admin"><AdminLeftBehind/></ProtectedRoute>}/>
            <Route path="/admin/ai-spend" element={<ProtectedRoute role="admin"><AdminAISpend/></ProtectedRoute>}/>
            <Route path="/admin/vendor-bills" element={<ProtectedRoute role="admin"><AdminVendorBills/></ProtectedRoute>}/>
            <Route path="/truck-stock" element={<ProtectedRoute><TruckStock/></ProtectedRoute>}/>
            <Route path="/admin/truck-stock" element={<ProtectedRoute role="admin"><AdminTruckStockRoster/></ProtectedRoute>}/>
            <Route path="/admin/backlog" element={<ProtectedRoute role="admin"><AdminBacklog/></ProtectedRoute>}/>
            <Route path="/admin/integrations/kraaft" element={<ProtectedRoute role="admin"><AdminKraaft/></ProtectedRoute>}/>
            <Route path="/wo/:token" element={<PublicWoStatus/>}/>
            <Route path="/portal/client/:token" element={<ClientPortalPublic/>}/>
            <Route path="/admin/imports/appfolio" element={<ProtectedRoute role="admin"><AdminAppfolioImport/></ProtectedRoute>}/>
            <Route path="/admin/imports/grok" element={<ProtectedRoute role="admin"><AdminGrokImport/></ProtectedRoute>}/>
            <Route path="/admin/settings/inspection-templates" element={<ProtectedRoute role="admin"><AdminInspectionTemplates/></ProtectedRoute>}/>
            <Route path="/m/:token" element={<MagicLogin/>}/>
            <Route path="/proof/:token" element={<ProofGallery/>}/>
            <Route path="/activity/:token" element={<ActivityReportPublic/>}/>
            <Route path="/property-fingerprint" element={<ProtectedRoute><PropertyFingerprint/></ProtectedRoute>}/>
            <Route path="/admin/properties/:address/board" element={<ProtectedRoute role="admin"><PropertyBoard/></ProtectedRoute>}/>
          </Routes>
          </BoundaryWithLocation>
        </BrowserRouter>
          </VendorFilterProvider>
        </IndustryProvider>
      </TenantProvider>
    </AuthProvider>
  );
}

export default App;
